import { fetchAllMarkets } from '$lib/apis/markets';
import { marketsConfig } from '$lib/config/markets';
import {
	markets,
	pythPrices,
	totalMarketOpenInterest,
	totalMarketTrades,
	totalMarketVolume
} from '$lib/home/store';
import { addErrorNotification } from '$lib/notifications/notificationManager';
import type { Market } from '$lib/types/market';
import { getMarketPrice, getNumBackInUnits, getPriceFromPythPrice, toBN } from '$lib/util/util';
import BigNumber from 'bignumber.js';
import { get } from 'svelte/store';
import { updateMarketCache } from './pyth';

export function setMarketWithData(data: Array<Market>) {
	const marketsToShow = Object.keys(marketsConfig)
		.filter((m) => marketsConfig[m].active)
		.map((m) => {
			if (m == 'sETH' || m == 'sBTC') {
				return m + 'PERP';
			}
			return 's' + m + 'PERP';
		});
	data = data.filter((m) => marketsToShow.includes(m?.marketIdentifier));

	data = data.sort((a: Market, b: Market) => {
		return new BigNumber(b?.volume24h ?? 0).minus(a?.volume24h ?? 0).toNumber();
	});
	let totalTrades: number = 0;
	let totalVolume: string = '0';
	let totalOpenInterest: string = '0';
	let currentPythPrices = get(pythPrices);
	data = data.sort((a: Market, b: Market) => {
		return new BigNumber(b?.volume24h ?? 0).minus(a?.volume24h ?? 0).toNumber();
	});
	data.forEach((m) => {
		totalTrades = new BigNumber(totalTrades).plus(m?.trades24h ?? 0).toNumber();
		totalVolume = new BigNumber(totalVolume).plus(m?.volume24h ?? 0).toString();
		totalOpenInterest = new BigNumber(totalOpenInterest).plus(m?.openInterest ?? 0).toString();

		if (currentPythPrices && currentPythPrices.hasOwnProperty(m?.asset)) {
			m.currentOraclePrice = getPriceFromPythPrice(currentPythPrices[m?.asset]);
			m.currentMarketPrice = getMarketPrice(m);

			m.maxMarketValueUSD = getNumBackInUnits(
				new BigNumber(m.maxMarketValue).times(m.currentMarketPrice).div(1e18).toString(),
				18,
				2
			);
			m.shortOIUSD = getNumBackInUnits(
				new BigNumber(m.shortOI).times(m.currentMarketPrice).div(1e18).toString(),
				18,
				2
			);
			m.longOIUSD = getNumBackInUnits(
				new BigNumber(m.longOI).times(m.currentMarketPrice).div(1e18).toString(),
				18,
				2
			);
			const change24h = new BigNumber(m.currentMarketPrice).minus(new BigNumber(m?.price24hAgo));
			const change24hPercentage = change24h
				.div(new BigNumber(m.currentMarketPrice))
				.multipliedBy(100)
				.toFixed(2);
			m.change24hPercentage = change24hPercentage;
			m.change24h = getNumBackInUnits(
				change24h.toFixed(8),
				18,
				marketsConfig[m?.asset].showDecimals
			);
			// // calculating skew
			// m.skew = new BigNumber(m.longOI).minus(m.shortOI);

			// // calculating discount / premium
			// m.discountOrPremium = new BigNumber(m.skew).div(m.skewScale);
		} else {
			// this assume the layout.ts pyth price fetch fails. If it succeeds, then this will be overwritten
			m.currentOraclePrice = '0';
			m.currentMarketPrice = '0';
			m.change24h = '0';
			m.maxMarketValueUSD = '0';
			m.shortOI = '0';
			m.longOIUSD = '0';
			m.change24hPercentage = '0';
			// m.skew = new BigNumber('0');
			// m.discountOrPremium = new BigNumber('0');
		}
	});
	updateMarketCache(data);
	markets.set(data);
	totalMarketTrades.set(totalTrades);
	totalMarketVolume.set(totalVolume);
	totalMarketOpenInterest.set(totalOpenInterest);
}

export async function getAllMarkets() {
	try {
		let data: Array<Market> = (await fetchAllMarkets()) as Market[];
		setMarketWithData(data);
	} catch (e) {
		addErrorNotification(
			'Failed to load markets',
			'An error occurred while fetching data. Please refresh the page.',
			'An error occurred while fetching data. Please refresh the page.',
			true
		);
	}
}
